import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAllAnswersByCampaign, getCampaignBenchmarks, getQuestionByCampaign, isUserActive } from '../service/brc';
import { downloadCSVFile, generateCSVData } from '../utils/csvExport';
import { BASE_URL } from '../config/global';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, Tooltip, Legend, ChartDataLabels, annotationPlugin);

const formatLift = (value) => {
  if (value == null) return 'N/A';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}pp`;
};

const formatRelativeLift = (value) => {
  if (value == null) return 'N/A';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const formatMoE = (value) => {
  if (value == null) return 'N/A';
  return `\u00B1${value.toFixed(2)}pp`;
};

const formatConfidence = (isSignificant, confidenceLevel) => {
  if (!isSignificant) return 'Not significant';
  return `${(confidenceLevel * 100).toFixed(0)}%`;
};

const BenchmarkCard = ({ benchmark, country, industry, kpi }) => {
  if (!benchmark || !benchmark.pooledMetrics) return null;

  const pm = benchmark.pooledMetrics;
  const dimensions = [country, industry, kpi].filter(Boolean).join(' \u2014 ');

  return (
    <div style={{
      marginTop: '10px',
      padding: '10px 14px',
      border: '1px solid #d6e4f0',
      borderRadius: '4px',
      backgroundColor: '#f5f8fc',
      fontSize: '13px',
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#4472C4' }}>
        Vertical Benchmark
        {dimensions && (
          <span style={{ fontWeight: 'normal', color: '#495057', marginLeft: '8px' }}>
            {dimensions}
          </span>
        )}
        <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
          | Based on {benchmark.campaignCount} campaigns
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ padding: '2px 8px', color: '#495057' }}>Absolute Lift</td>
            <td style={{ padding: '2px 8px', fontWeight: '600' }}>{formatLift(pm.absoluteLift)}</td>
            <td style={{ padding: '2px 8px', color: '#495057' }}>Relative Lift</td>
            <td style={{ padding: '2px 8px', fontWeight: '600' }}>{formatRelativeLift(pm.relativeLift)}</td>
          </tr>
          <tr>
            <td style={{ padding: '2px 8px', color: '#495057' }}>Margin of Error</td>
            <td style={{ padding: '2px 8px', fontWeight: '600' }}>{formatMoE(pm.marginOfError)}</td>
            <td style={{ padding: '2px 8px', color: '#495057' }}>Statistical Confidence</td>
            <td style={{ padding: '2px 8px', fontWeight: '600' }}>{formatConfidence(pm.isStatisticallySignificant, pm.confidenceLevel)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const Reports = () => {
  const { state } = useLocation();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});
  const [benchmarkMeta, setBenchmarkMeta] = useState({ country: '', industry: '' });
  const [showNumberOfVotes, setShowNumberOfVotes] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();

  const numberFormatter = (num) => {
    const format = new Intl.NumberFormat("en", {
      notation: "compact",
      compactDisplay: "short",
    });
    return format.format(num).replace("G", "B");
  };

  const campaignId = state?.campaignId || state?.id || '';

  const exportToCSV = () => {
    const campaignName = state?.campaignName || '';
    const filename = `${campaignName || 'Report'}.csv`;

    if (!campaignId || !campaignName) {
      return;
    }

    const csvData = generateCSVData(
      { campaignId, campaignName },
      questions,
      answers,
      benchmarks,
      benchmarkMeta
    );

    downloadCSVFile(csvData, filename);
  };

  const exportToPDF = () => {
    const input = document.getElementById('chartReportContent');
    const mainTitleElement = document.getElementById('chartReportTitleMain');
    const titleElement = document.getElementById('chartReportTitle');
    const childElements = input.children;

    const pdf = new jsPDF('p', 'mm', 'a4');

    const scalingMargin = 10;
    const spacingMargin = 20;
    const pageWidth = 210;
    const pageHeight = 297;
    const usablePageHeight = pageHeight - 2 * scalingMargin;
    let currentY = scalingMargin;

    const addPageContent = async (element, currentPageHeight) => {
      const canvas = await html2canvas(element);
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - 2 * scalingMargin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      if (currentY + imgHeight > currentPageHeight) {
        pdf.addPage();
        currentY = scalingMargin;
      }
      pdf.addImage(imgData, 'PNG', scalingMargin, currentY, imgWidth, imgHeight);
      currentY += imgHeight + spacingMargin;
    };

    const processElements = async () => {
      mainTitleElement.style.display = 'none';
      titleElement.style.display = 'block';
      await addPageContent(titleElement, usablePageHeight);
      for (let i = 0; i < childElements.length; i++) {
        await addPageContent(childElements[i], usablePageHeight);
      }
      titleElement.style.display = 'none';
      mainTitleElement.style.display = 'block';
      pdf.save(`${state ? state.campaignName : 'Report'}.pdf`);
    };

    processElements().catch((err) => {
      console.error(err);
    });
  };

  const userCheck = async () => {
    const response = await isUserActive();
    if (response && !response.data) {
      navigate('/Login');
      window.location.reload();
    }
  };

  const returnToCampaigns = () => {
    navigate('/');
  };

  let m = {};
  let allAnswers = [];

  const barCharts = questions.map((question, i) => {
    let barData = {},
    barOptions = {};

    const labels = answers
    .filter((obj) => obj.questionId === question._id)
    .map((filteredObj) => (filteredObj.isCorrect ? '(Preferred) ' + filteredObj.answer : filteredObj.answer));

    const calculateTotal = (counts) => counts.reduce((acc, val) => acc + val, 0);

    const controlCounts = answers.filter((obj) => obj.questionId === question._id).map((filteredObj) => filteredObj.controlCount);
    const exposedCounts = answers.filter((obj) => obj.questionId === question._id).map((filteredObj) => filteredObj.exposedCount);
    const totalControl = calculateTotal(controlCounts);
    const totalExposed = calculateTotal(exposedCounts);
    
    if(question.controlView){

      barData = {
        labels: labels,
        datasets: [
          {
            label: 'Control',
            data: !showNumberOfVotes ? controlCounts.map(count => (count / totalControl * 100).toFixed(2)) : controlCounts,
            backgroundColor: '#02075d',
          },
          {
            label: 'Exposed',
            data: !showNumberOfVotes ? exposedCounts.map(count => (count / totalExposed * 100).toFixed(2)) : exposedCounts,
            backgroundColor: 'rgb(128,128,128)',
          }
        ],
      };
    } else {
      const counts = answers.filter((obj) => obj.questionId === question._id).map((filteredObj) => filteredObj.count);
      const totalCounts = calculateTotal(counts);

      barData = {
        labels: labels,
        datasets: [
          {
            label: 'Responses',
            data: !showNumberOfVotes ? counts.map(count => (count / totalCounts * 100).toFixed(2)) : counts,
            backgroundColor: '#02075d',
          }
        ],
      };
    }

     const annotations = labels.reduce((acc, label, index) => {
      const controlPercentage = (controlCounts[index] / totalControl) * 100;
      const exposedPercentage = (exposedCounts[index] / totalExposed) * 100;
      const uplift = (exposedPercentage - controlPercentage).toFixed(2);
      const maxYValue = Math.max(controlCounts[index], exposedCounts[index]);
      const yOffset = 8.5;

      acc[`upliftLabel${index}`] = {
        type: 'label',
        xValue: index,
        yValue:  maxYValue + yOffset,
        content: `${uplift > 0 ? 'Uplift' : 'Downlift'}: ${Math.abs(uplift)}%`,
        backgroundColor: uplift > 0 ? 'rgba(0, 255, 0, 0.25)' : 'rgba(255, 0, 0, 0.25)',
        color: 'black',
        position: 'center',
      };
      return acc;
    }, {});

    barOptions = {
      plugins: {
        legend: {
          position: 'top',
        },
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'end',
          color: 'black',
          font: {
            weight: 'bold',
            size: "12"
          },
          padding: {
            top: -10
          },
          formatter: (value, context) => {
            return !showNumberOfVotes ? `${value}%` : numberFormatter(value);
          },
        },
        annotation: {
          annotations: !showNumberOfVotes && showResults ? annotations : []
        }
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
      responsive: true,
    };

    return (
      <div key={question.question} style={{width: '100%', marginTop: '5%'}}>
        <h6 style={{display: question.brandMetricType !== '' && showMetrics ? 'block' : 'none' }}>{question.brandMetricType}</h6>
        <div>{question.question}</div>
        <Bar options={barOptions} data={barData}></Bar>
        {question.controlView && <BenchmarkCard benchmark={benchmarks[question._id]} country={benchmarkMeta.country} industry={benchmarkMeta.industry} kpi={question.brandMetricType} />}
      </div>
    );
  });

  useEffect(() => {
    userCheck();
    if (state === undefined || state === null || state === '') {
      navigate('/');
    } else {
      getQuestionByCampaign(campaignId).then((res) => {
        // _data is a flat question array; campaignSummary is a sibling field
        const responseData = res.data._data;
        const questionList = Array.isArray(responseData) ? responseData : (responseData?.questions || []);
        const targetId = state.campaignId || state.id;
        setQuestions(questionList.filter((x) => x.campaignId === targetId).reverse());
      });
    }
  }, [campaignId]);

  useEffect(() => {
    getAllAnswersByCampaign(campaignId).then((res) => {
      const fetchedAnswers = res.data._data;
      setAnswers(fetchedAnswers);

      for (let i = 0; i < questions.length; i++) {
        const questionId = questions[i]._id;
        let ans = [];
        for (let j = 0; j < fetchedAnswers.length; j++) {
          if (fetchedAnswers[j].questionId === questionId) {
            ans.push(fetchedAnswers[j]);
          }
        }
        m[questionId] = ans;
        allAnswers.push(ans);
      }
    });
  }, [questions, campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    getCampaignBenchmarks(campaignId).then((res) => {
      const data = res?.data?._data;
      if (data?.benchmarks) {
        setBenchmarks(data.benchmarks);
        setBenchmarkMeta({ country: data.country || '', industry: data.industry || '' });
      }
    }).catch((err) => console.warn('Benchmark fetch failed:', err));
  }, [campaignId]);

  return (
    <div>
      <Button
        style={{
          fontWeight: 'bold',
          margin: '10px',
          float: 'left',
        }}
        variant='secondary'
        onClick={() => returnToCampaigns()}>
        Return to Campaigns
      </Button>
      <Button
        style={{
          fontWeight: 'bold',
          margin: '10px',
        }}
        variant='primary'
        onClick={() => {
          setShowNumberOfVotes(!showNumberOfVotes)
          setShowResults((showNumberOfVotes && showResults) ? false : (!showNumberOfVotes && showResults) ? true : false)
        }}>
        {!showNumberOfVotes ? 'Show # of Responses' : 'Hide # of Responses'}
      </Button>
      <Button
        style={{
          fontWeight: 'bold',
          margin: '10px',
        }}
        variant='primary'
        onClick={() => setShowMetrics(!showMetrics)}>
        {showMetrics ? 'Hide Metrics' : 'Show Metrics'}
      </Button>
      {!showNumberOfVotes ?
        <Button
          style={{
            fontWeight: 'bold',
            margin: '10px',
          }}
          variant='primary'
          onClick={() => setShowResults(!showResults)}>
          {showResults ? 'Hide Results' : 'Show Results'}
        </Button> : <></>}
      <Dropdown style={{ float: 'right', margin: '10px' }}>
        <Dropdown.Toggle
          variant="secondary"
          id="download-dropdown"
          style={{ fontWeight: 'bold' }}>
          Download Campaign Report
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => exportToPDF()}>
            PDF
          </Dropdown.Item>
          <Dropdown.Item onClick={() => exportToCSV()}>
            CSV
          </Dropdown.Item>
          <Dropdown.Item as="a" href={`${BASE_URL}/campaigns/${campaignId}/full-report?format=xlsx`} target="_blank">
            XLSX
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <div style={{ width: '60%', margin: 'auto', marginBottom: '3%' }}>
        <div id='chartReportTitleMain' style={{width: '100%', paddingTop: '10%'}}>
          <h2>{state ? state.campaignName : ''}</h2>
        </div>
        <div id="chartReportTitle" style={{ display: 'none' }}>
          <h2>{state ? state.campaignName : ''}</h2>
        </div>
        <div id='chartReportContent' style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center' }}>{barCharts}</div>
      </div>
    </div>
  );
};

export default Reports;
